/**
 * Auth slice — manages user, token, and authentication state in Redux.
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  login as apiLogin,
  logout as apiLogout,
  fetchCurrentUser,
  getToken,
  getUser,
  clearAuth,
  isMfaRequiredResponse,
} from '@/lib/auth';
import type { User, LoginRequest } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Discriminated result of the `loginUser` thunk.
 * `mfa` carries the short-lived single-use challenge JWT (`preAuthJwt` —
 * exact backend field name) plus its `status`; it lives in the Redux store
 * (memory) only and must NEVER be written to localStorage.
 */
export type LoginResult =
  | { kind: 'ok'; user: User; token: string }
  | {
      kind: 'mfa';
      status: 'NOT_ENROLLED' | 'ENROLLED';
      preAuthJwt: string;
      preAuthTtlSeconds: number;
    };

const initialState: AuthState = {
  user: getUser(),
  token: getToken(),
  isAuthenticated: !!getToken(),
  isLoading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await apiLogin(credentials);
      if (isMfaRequiredResponse(response)) {
        // No tokens were issued; the MFA flow (mfaSlice) takes over from here.
        // Enrollment is derived from the wire `status` field (NOT_ENROLLED →
        // enrollment flow; ENROLLED → verification flow).
        return {
          kind: 'mfa',
          status: response.status,
          preAuthJwt: response.preAuthJwt,
          preAuthTtlSeconds: response.preAuthTtlSeconds,
        } satisfies LoginResult;
      }
      const user = await fetchCurrentUser();
      return { kind: 'ok', user, token: response.accessToken } satisfies LoginResult;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      return rejectWithValue(message);
    }
  },
);

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  await apiLogout();
});

export const loadCurrentUser = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const user = await fetchCurrentUser();
    return user;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to load user';
    return rejectWithValue(message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuth(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      clearAuth();
    },
    clearError(state) {
      state.error = null;
    },
    /**
     * A session was completed through an out-of-band flow (e.g. the MFA steps
     * in mfaSlice) that already persisted real tokens via lib/auth.
     */
    sessionEstablished(state, action: PayloadAction<{ user: User; token: string }>) {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResult>) => {
        state.isLoading = false;
        if (action.payload.kind === 'ok') {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
        }
        // 'mfa': stay unauthenticated; mfaSlice owns the pending step.
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Login failed';
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      // Load current user
      .addCase(loadCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.user = action.payload;
      })
      .addCase(loadCurrentUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        clearAuth();
      });
  },
});

export const { resetAuth, clearError, sessionEstablished } = authSlice.actions;
export default authSlice.reducer;
